'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { EDIT_FORBIDDEN, guardExport } from '@/contexts/pipeline-v2/edits/guardAction.server';

export async function exportIllustrationPdf(input: {
  bookId: string;
  storyId: string;
  imagesOnly?: boolean;
  /** HD DOWNLOAD export — upscales pages to print DPI and DEBITS the allowance. */
  hd?: boolean;
  /** Paper for the HD export: 'trim' = the book's own trim size (default), 'a4' = an A4 sheet. */
  paper?: 'trim' | 'a4';
  /** Paint the rest of the sheet with the artwork's own mirrored edges instead
   *  of leaving white. An A4 sheet never matches the artwork, so it implies this. */
  edgeFill?: boolean;
  /** ZIP of the same sheets, one file each — what a print shop asks for. */
  archive?: boolean;

}): Promise<ActionResult<{ jobId?: string; pdfUrl?: string }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };

    // stale pages / audio: «сначала обновите»
    const stale = await guardExport({ storyId: input.storyId, familyId, kind: 'pdf' });
    if (stale) return { success: false, error: stale.message, code: EDIT_FORBIDDEN };

    const { MediaExportService } = await import('@/contexts/media/export/MediaExportService');

    const result = await MediaExportService.startPdfExport({
      bookId: input.bookId,
      familyId,
      storyId: input.storyId,
      imagesOnly: input.imagesOnly ?? true,
      hd: input.hd ?? false,
      paper: input.paper,
      edgeFill: input.edgeFill,
      archive: input.archive,
    });

    if (!result.success) {
      return { success: false, error: result.error || 'EXPORT_FAILED' };
    }

    return { success: true, data: { jobId: result.jobId } };
  } catch (e: unknown) {
    // HD reserve → out-of-allowance throws "Insufficient tokens (billing:
    // insufficient_allowance)". Surface a discriminated upsell variant (mirrors
    // createContentPlan) instead of a raw error so the UI shows the plans CTA.
    if (isInsufficientAllowance(e)) {
      return { success: false, error: 'insufficient_allowance', code: 'INSUFFICIENT_ALLOWANCE' };
    }
    return { success: false, error: actionErrorMessage('exportBookPdf', e) };
  }
}

/** Duck-typed guard: the billing adapter throws "Insufficient tokens (billing: insufficient_allowance)". */
function isInsufficientAllowance(e: unknown): boolean {
  const m = e instanceof Error ? e.message : String(e);
  return /insufficient/i.test(m) && /(tokens|allowance)/i.test(m);
}
